/**
 * TokenManager - Gestionnaire de tokens en mémoire
 *
 * ISO 27001 A.9.4.2 - Protection des tokens d'authentification
 *
 * ARCHITECTURE SIMPLIFIÉE (Janvier 2026):
 * - Access Token: Mémoire JavaScript uniquement (perdu au F5, c'est normal)
 * - Refresh Token: Cookie HttpOnly géré par le backend (invisible au JS)
 * - User Data: Zustand store (pas ici)
 *
 * Après F5: Le frontend appelle /refresh, le cookie est envoyé automatiquement
 * par le navigateur, et le backend retourne un nouvel access token.
 */

import { logger } from '../utils/logger';

// ============================================================================
// ACCESS TOKEN - MÉMOIRE UNIQUEMENT
// ============================================================================

/**
 * Access token stocké en mémoire JavaScript uniquement.
 * Non accessible via XSS car jamais écrit dans le DOM/Storage.
 */
let accessToken: string | null = null;

/**
 * Timestamp d'expiration de l'access token (millisecondes)
 */
let tokenExpiry: number | null = null;

/**
 * Stocke l'access token en mémoire
 */
export function setAccessToken(token: string, expiresIn: number): void {
  accessToken = token;
  tokenExpiry = Date.now() + expiresIn * 1000;

  if (import.meta.env.DEV) {
    logger.log('🔐 TokenManager: Access token stored in memory (expires in', expiresIn, 's)');
  }
}

/**
 * Récupère l'access token depuis la mémoire
 * Retourne null si expiré
 */
export function getAccessToken(): string | null {
  if (tokenExpiry && Date.now() >= tokenExpiry) {
    if (import.meta.env.DEV) {
      logger.log('🔐 TokenManager: Access token expired, clearing');
    }
    accessToken = null;
    tokenExpiry = null;
    return null;
  }

  if (import.meta.env.DEV) {
    logger.log('🔐 TokenManager.getAccessToken:', {
      hasToken: !!accessToken,
      tokenExpiry: tokenExpiry ? new Date(tokenExpiry).toISOString() : null,
      timeUntilExpiry: tokenExpiry ? Math.floor((tokenExpiry - Date.now()) / 1000) + 's' : null,
    });
  }

  return accessToken;
}

/**
 * Vérifie si l'access token existe et n'est pas expiré
 */
export function hasValidAccessToken(): boolean {
  return getAccessToken() !== null;
}

/**
 * Vérifie si l'access token va expirer bientôt
 * @param thresholdSeconds - Seuil en secondes (défaut: 60s)
 */
export function isAccessTokenExpiringSoon(thresholdSeconds: number = 60): boolean {
  if (!tokenExpiry) return true;

  const timeUntilExpiry = tokenExpiry - Date.now();
  return timeUntilExpiry <= thresholdSeconds * 1000;
}

/**
 * Retourne le temps restant avant expiration en secondes
 */
export function getTimeUntilExpiry(): number {
  if (!tokenExpiry) return 0;

  const timeUntilExpiry = tokenExpiry - Date.now();
  return Math.max(0, Math.floor(timeUntilExpiry / 1000));
}

/**
 * Efface l'access token de la mémoire
 */
export function clearAccessToken(): void {
  accessToken = null;
  tokenExpiry = null;

  if (import.meta.env.DEV) {
    logger.log('🔐 TokenManager: Access token cleared from memory');
  }
}

// ============================================================================
// SESSION HELPERS
// ============================================================================

/**
 * Vérifie si une session PEUT exister
 * Note: On ne peut pas vérifier le cookie HttpOnly depuis JS
 * Cette fonction retourne toujours true pour permettre une tentative de refresh
 */
export function hasSession(): boolean {
  // On ne peut pas savoir si le cookie existe depuis JS
  // On retourne true pour toujours tenter un refresh au démarrage
  // Si le cookie n'existe pas, le refresh échouera et on redirigera vers login
  return true;
}

/**
 * Efface toute la session (côté client uniquement)
 * Note: Le cookie HttpOnly est supprimé par le backend via /logout
 */
export function clearSession(): void {
  clearAccessToken();

  // Nettoyer les anciennes clés sessionStorage (migration)
  sessionStorage.removeItem('access_token');
  sessionStorage.removeItem('refresh_token');
  sessionStorage.removeItem('token_expiry');
  sessionStorage.removeItem('user');
  sessionStorage.removeItem('rt_secure');
  sessionStorage.removeItem('user_data');
  sessionStorage.removeItem('auth-storage');

  if (import.meta.env.DEV) {
    logger.log('🔐 TokenManager: Session cleared');
  }
}

// ============================================================================
// EXPORT DEFAULT POUR COMPATIBILITÉ
// ============================================================================

const TokenManager = {
  // Access Token (mémoire uniquement)
  setAccessToken,
  getAccessToken,
  hasValidAccessToken,
  isAccessTokenExpiringSoon,
  getTimeUntilExpiry,
  clearAccessToken,

  // Session
  hasSession,
  clearSession,
};

export default TokenManager;

// ============================================================================
// DEPRECATED - Pour rétrocompatibilité pendant migration
// ============================================================================

/** @deprecated Ne plus utiliser - le refresh token est dans un cookie HttpOnly */
export function getRefreshToken(): string | null {
  return null;
}

/** @deprecated Ne plus utiliser - le refresh token est dans un cookie HttpOnly */
export function setRefreshToken(_token: string): void {
  logger.warn('setRefreshToken is deprecated - refresh token is in HttpOnly cookie');
}

/** @deprecated Ne plus utiliser - user data est dans Zustand */
export function getUserData(): any {
  return null;
}

/** @deprecated Ne plus utiliser - user data est dans Zustand */
export function setUserData(_user: any): void {
  logger.warn('setUserData is deprecated - use authStore instead');
}

/** @deprecated Ne plus utiliser - user data est dans Zustand */
export function updateUserData(_user: any): void {
  logger.warn('updateUserData is deprecated - use authStore instead');
}

/** @deprecated Alias pour getUserData */
export const getUser = getUserData;

/** @deprecated Alias pour updateUserData */
export const updateUser = updateUserData;

/** @deprecated Ne plus utiliser */
export function setSession(
  newAccessToken: string,
  _refreshToken: string,
  expiresIn: number,
  _user: any
): void {
  // On ne stocke que l'access token en mémoire
  setAccessToken(newAccessToken, expiresIn);
  logger.warn('setSession is deprecated - only access token is stored in memory now');
}

/** @deprecated Utiliser setAccessToken directement */
export function updateAccessToken(token: string, expiresIn: number): void {
  setAccessToken(token, expiresIn);
}
